import { useState } from 'react'
import {
  Box,
  Flex,
  VStack,
  Text,
  Input,
  Button,
  FormControl,
  FormLabel,
  InputGroup,
  InputRightElement,
  IconButton,
  Alert,
  AlertIcon,
  AlertDescription,
  ScaleFade,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Link,
} from '@chakra-ui/react'
import { Eye, EyeSlash, ChatTeardropDots } from '@phosphor-icons/react'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [forgotEmail, setForgotEmail] = useState('')
  const [forgotSuccess, setForgotSuccess] = useState(false)
  const [forgotLoading, setForgotLoading] = useState(false)
  const [forgotError, setForgotError] = useState('')
  const { isOpen: isForgotOpen, onOpen: onForgotOpen, onClose: onForgotClose } = useDisclosure()
  const { login } = useAuth()
  const navigate = useNavigate()

  const pageBg = useColorModeValue('gray.50', 'gray.900')
  const surfaceBg = useColorModeValue('white', 'gray.800')
  const pageText = useColorModeValue('gray.800', 'white')
  const secondaryText = useColorModeValue('gray.600', 'gray.300')
  const mutedText = useColorModeValue('gray.500', 'gray.400')
  const iconOnBrand = useColorModeValue('gray.800', 'gray.900')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMessage('')

    if (!email || !password) {
      setErrorMessage('Preencha todos os campos para continuar.')
      return
    }

    setIsLoading(true)
    try {
      const success = await login(email, password)

      if (success) {
        navigate('/tickets')
      } else {
        setErrorMessage('Email ou senha inválidos. Verifique suas credenciais e tente novamente.')
      }
    } catch {
      setErrorMessage('Não foi possível conectar ao servidor. Tente novamente mais tarde.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleForgotOpen = () => {
    setForgotEmail('')
    setForgotSuccess(false)
    setForgotError('')
    onForgotOpen()
  }

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = forgotEmail.trim()
    if (!EMAIL_REGEX.test(trimmed)) {
      setForgotError('Digite um email válido.')
      return
    }
    setForgotLoading(true)
    setForgotError('')
    try {
      await api.post('/auth/forgot-password', { email: trimmed })
    } catch {
      // Sempre ignoramos erro (usuário não existir, etc.) por segurança
    } finally {
      setForgotLoading(false)
      setForgotSuccess(true)
    }
  }

  return (
    <Flex
      minH="100vh"
      w="100%"
      align="center"
      justify="center"
      bg={pageBg}
      p={4}
    >
      <Box
        w="100%"
        maxW="400px"
        bg={surfaceBg}
        borderRadius="2xl"
        p={8}
        boxShadow="xl"
      >
        <VStack spacing={8}>
          {/* Logo */}
          <VStack spacing={3}>
            <Box
              w={16}
              h={16}
              bg="yellow.500"
              borderRadius="2xl"
              display="flex"
              alignItems="center"
              justifyContent="center"
              color={iconOnBrand}
            >
              <ChatTeardropDots size={40} color="currentColor" weight="fill" />
            </Box>
            <VStack spacing={1}>
              <Text fontSize="2xl" fontWeight="bold" color={pageText}>
                WeDrop Suporte
              </Text>
              <Text fontSize="sm" color={mutedText}>
                Painel do Atendente
              </Text>
            </VStack>
          </VStack>

          {/* Error Alert */}
          <ScaleFade in={!!errorMessage} unmountOnExit>
            <Alert
              status="error"
              borderRadius="lg"
              bg="red.900"
              border="1px solid"
              borderColor="red.600"
              w="100%"
            >
              <AlertIcon color="red.300" />
              <AlertDescription color="red.200" fontSize="sm">
                {errorMessage}
              </AlertDescription>
            </Alert>
          </ScaleFade>

          {/* Form */}
          <Box as="form" w="100%" onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <FormControl isInvalid={!!errorMessage}>
                <FormLabel color={secondaryText}>Email</FormLabel>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setErrorMessage('') }}
                  placeholder="seu@email.com"
                  size="lg"
                  variant="outline"
                />
              </FormControl>

              <FormControl isInvalid={!!errorMessage}>
                <FormLabel color={secondaryText}>Senha</FormLabel>
                <InputGroup size="lg">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setErrorMessage('') }}
                    placeholder="••••••••"
                    variant="outline"
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                      icon={showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowPassword(!showPassword)}
                      color={mutedText}
                      _hover={{ color: pageText }}
                    />
                  </InputRightElement>
                </InputGroup>
              </FormControl>

              <Button
                type="submit"
                w="100%"
                size="lg"
                bg="yellow.500"
                color="gray.900"
                _hover={{ bg: 'yellow.400' }}
                isLoading={isLoading}
                loadingText="Entrando..."
                mt={4}
              >
                Entrar
              </Button>

              <Link
                fontSize="sm"
                color={mutedText}
                _hover={{ color: 'yellow.500', textDecoration: 'underline' }}
                onClick={handleForgotOpen}
                cursor="pointer"
              >
                Esqueci minha senha
              </Link>
            </VStack>
          </Box>

          {/* Modal Esqueci minha senha */}
          <Modal isOpen={isForgotOpen} onClose={onForgotClose} isCentered size="sm">
            <ModalOverlay />
            <ModalContent bg={surfaceBg}>
              <ModalHeader color={pageText}>Redefinir senha</ModalHeader>
              <ModalCloseButton color={mutedText} />
              <ModalBody pb={6}>
                {forgotSuccess ? (
                  <Alert
                    status="success"
                    borderRadius="lg"
                    bg="green.900"
                    border="1px solid"
                    borderColor="green.600"
                  >
                    <AlertIcon color="green.300" />
                    <AlertDescription color="green.200" fontSize="sm">
                      Se o email digitado for válido, você receberá um email de redefinição de senha.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Box as="form" onSubmit={handleForgotSubmit}>
                    <VStack spacing={4} align="stretch">
                      <Text fontSize="sm" color={secondaryText}>
                        Informe seu email. Enviaremos um link para redefinir sua senha.
                      </Text>
                      {forgotError && (
                        <ScaleFade in>
                          <Alert status="error" size="sm" borderRadius="lg">
                            <AlertIcon />
                            <AlertDescription fontSize="sm">{forgotError}</AlertDescription>
                          </Alert>
                        </ScaleFade>
                      )}
                      <FormControl isInvalid={!!forgotError}>
                        <FormLabel color={secondaryText}>Email</FormLabel>
                        <Input
                          type="email"
                          value={forgotEmail}
                          onChange={(e) => { setForgotEmail(e.target.value); setForgotError('') }}
                          placeholder="seu@email.com"
                          size="lg"
                          variant="outline"
                          isRequired
                        />
                      </FormControl>
                      <Button
                        type="submit"
                        w="100%"
                        size="lg"
                        bg="yellow.500"
                        color="gray.900"
                        _hover={{ bg: 'yellow.400' }}
                        isLoading={forgotLoading}
                        loadingText="Enviando..."
                      >
                        Enviar link de redefinição
                      </Button>
                    </VStack>
                  </Box>
                )}
              </ModalBody>
            </ModalContent>
          </Modal>

          {/* Info */}
          <Text fontSize="xs" color={mutedText} textAlign="center">
            Use suas credenciais da plataforma WeDrop.
            <br />
            Você precisa ter permissão de atendente para acessar.
          </Text>
        </VStack>
      </Box>
    </Flex>
  )
}
